import { projectsApi } from "../api/projects";
import {
  hasGuidedProjectStart,
  isProjectCapabilityEnabled,
  normalizeProjectBusinessProfile,
  projectBusinessGoals,
  projectStarterAreas,
} from "./projectCapabilities";
import { isTrialProject } from "./trialOnboarding";
import {
  DASHBOARD_AREA_KEYS,
  getProjectNavigationToolStarterAreas,
} from "./dashboardNavigation";

const STORAGE_PREFIX = "webprofit_project_navigation_v2";
const MAX_GUIDED_PENDING_AREAS = 3;
const MAX_NEW_TOOL_INDICATORS = 2;
const ITEM_KEY_RE = /^[a-z0-9][a-z0-9:_-]{0,79}$/i;

const PROJECT_AREA_KEYS = new Set([
  DASHBOARD_AREA_KEYS.HOME,
  DASHBOARD_AREA_KEYS.PAGE,
  DASHBOARD_AREA_KEYS.SALES,
  DASHBOARD_AREA_KEYS.CUSTOMERS,
  DASHBOARD_AREA_KEYS.OPERATION,
  DASHBOARD_AREA_KEYS.GASTRONOMY,
  DASHBOARD_AREA_KEYS.MANAGEMENT,
  DASHBOARD_AREA_KEYS.STATS,
  DASHBOARD_AREA_KEYS.SETTINGS,
  DASHBOARD_AREA_KEYS.MORE,
]);

const AREA_STARTER_AREAS = Object.freeze({
  [DASHBOARD_AREA_KEYS.PAGE]: Object.freeze(["landing"]),
  [DASHBOARD_AREA_KEYS.SALES]: Object.freeze(["items", "orders"]),
  [DASHBOARD_AREA_KEYS.CUSTOMERS]: Object.freeze(["customers"]),
  [DASHBOARD_AREA_KEYS.OPERATION]: Object.freeze(["agenda"]),
  [DASHBOARD_AREA_KEYS.GASTRONOMY]: Object.freeze(["items", "agenda", "orders", "system"]),
  [DASHBOARD_AREA_KEYS.MANAGEMENT]: Object.freeze(["system", "items"]),
  [DASHBOARD_AREA_KEYS.STATS]: Object.freeze(["stats"]),
});

const CAPABILITY_DISCOVERY_TOOL_KEYS = Object.freeze({
  memberships: "customers-memberships",
  gastronomicMenu: "gastronomy-menu",
  tables: "gastronomy-tables",
  tableReservations: "gastronomy-reservations",
  kitchenOrders: "gastronomy-kitchen",
  recipes: "gastronomy-recipes",
});

const GOAL_DISCOVERY_TOOL_KEYS = Object.freeze({
  sellProducts: Object.freeze(["products", "orders"]),
  offerServices: Object.freeze(["agenda"]),
  manageCustomers: Object.freeze(["customers-crm"]),
  manageMemberships: Object.freeze(["customers-memberships"]),
  gastronomicMenu: Object.freeze(["gastronomy-menu"]),
  controlBusiness: Object.freeze(["overview", "movements"]),
});

const PROFILE_DISCOVERY_TOOL_KEYS = Object.freeze({
  commerce: Object.freeze(["products", "orders"]),
  services: Object.freeze(["agenda", "customers-crm"]),
  gym: Object.freeze(["customers-memberships", "customers-crm", "agenda"]),
  restaurant: Object.freeze(["gastronomy-menu", "gastronomy-tables", "gastronomy-kitchen"]),
});

const READINESS_MILESTONE_TOOL_KEYS = Object.freeze({
  customers: "customers-crm",
  memberships: "customers-memberships",
  gastronomicMenu: "gastronomy-menu",
  serviceReservations: "agenda",
  tables: "gastronomy-tables",
  recipes: "gastronomy-recipes",
  tableReservations: "gastronomy-reservations",
  orders: "orders",
  kitchenOrders: "gastronomy-kitchen",
  purchases: "movements",
  system: "overview",
});

function navigationItemAreas(itemKey) {
  return getProjectNavigationToolStarterAreas(itemKey);
}

function clean(value) {
  return String(value ?? "").trim();
}

function cleanItemKey(value) {
  const key = clean(value);
  return ITEM_KEY_RE.test(key) ? key : "";
}

function navigationUserKey(user) {
  return clean(user?.id || user?._id || user?.email).toLowerCase();
}

function pendingFact(facts, code) {
  const current = facts?.[code];
  return current?.required === true && current?.complete !== true;
}

function pendingMilestone(milestones, code) {
  const current = milestones?.[code];
  return Boolean(
    current &&
      current.complete !== true &&
      (current.required === true || current.roadmap === true),
  );
}

function addUnique(list, value, availableKeys = null) {
  const key = clean(value);
  if (!key || list.includes(key)) return;
  if (availableKeys && !availableKeys.has(key)) return;
  list.push(key);
}

function capabilityAnchorAreaKeys(project, availableKeys) {
  const result = [];

  if (isProjectCapabilityEnabled(project, "memberships")) {
    addUnique(result, DASHBOARD_AREA_KEYS.CUSTOMERS, availableKeys);
  }

  if (
    isProjectCapabilityEnabled(project, "gastronomicMenu") ||
    isProjectCapabilityEnabled(project, "tables") ||
    isProjectCapabilityEnabled(project, "tableReservations") ||
    isProjectCapabilityEnabled(project, "kitchenOrders") ||
    isProjectCapabilityEnabled(project, "recipes")
  ) {
    addUnique(result, DASHBOARD_AREA_KEYS.GASTRONOMY, availableKeys);
  }

  return result;
}

function readinessAreaKeys(project, availableKeys) {
  const readiness = project?.readiness;
  const facts = readiness?.facts && typeof readiness.facts === "object"
    ? readiness.facts
    : {};
  if (!Object.keys(facts).length) return [];

  const result = [];
  const add = (condition, key) => {
    if (condition) addUnique(result, key, availableKeys);
  };

  add(pendingFact(facts, "identity"), DASHBOARD_AREA_KEYS.SETTINGS);
  add(pendingFact(facts, "pageStarted"), DASHBOARD_AREA_KEYS.PAGE);
  add(pendingFact(facts, "firstItem"), DASHBOARD_AREA_KEYS.SALES);

  const operational = facts?.operationalManagement && typeof facts.operationalManagement === "object"
    ? facts.operationalManagement
    : {};
  const milestones = operational?.milestones && typeof operational.milestones === "object"
    ? operational.milestones
    : {};

  const milestoneAreas = [
    ["customers", DASHBOARD_AREA_KEYS.CUSTOMERS],
    ["memberships", DASHBOARD_AREA_KEYS.CUSTOMERS],
    ["gastronomicMenu", DASHBOARD_AREA_KEYS.GASTRONOMY],
    ["serviceReservations", DASHBOARD_AREA_KEYS.OPERATION],
    ["tables", DASHBOARD_AREA_KEYS.GASTRONOMY],
    ["recipes", DASHBOARD_AREA_KEYS.GASTRONOMY],
    ["tableReservations", DASHBOARD_AREA_KEYS.GASTRONOMY],
    ["orders", DASHBOARD_AREA_KEYS.SALES],
    ["kitchenOrders", DASHBOARD_AREA_KEYS.GASTRONOMY],
    ["purchases", DASHBOARD_AREA_KEYS.MANAGEMENT],
    ["system", DASHBOARD_AREA_KEYS.MANAGEMENT],
  ];

  for (const [code, areaKey] of milestoneAreas) {
    add(pendingMilestone(milestones, code), areaKey);
  }

  // Readiness anteriores no tenían milestones finos. Conservamos su evidencia
  // para que la navegación progresiva no dependa de recalcular inmediatamente.
  if (!Object.keys(milestones).length) {
    const targets = operational?.targets && typeof operational.targets === "object"
      ? operational.targets
      : {};
    const evidence = operational?.evidence && typeof operational.evidence === "object"
      ? operational.evidence
      : {};
    add(targets.customers === true && evidence.customers !== true, DASHBOARD_AREA_KEYS.CUSTOMERS);
    add(targets.reservations === true && evidence.reservations !== true, DASHBOARD_AREA_KEYS.OPERATION);
    add(targets.gastronomy === true && evidence.gastronomy !== true, DASHBOARD_AREA_KEYS.GASTRONOMY);
    add(targets.system === true && evidence.system !== true, DASHBOARD_AREA_KEYS.MANAGEMENT);
  }

  add(
    !pendingFact(facts, "pageStarted") &&
      (pendingFact(facts, "contact") || pendingFact(facts, "publicContent")),
    DASHBOARD_AREA_KEYS.PAGE,
  );
  add(pendingFact(facts, "paymentMethod"), DASHBOARD_AREA_KEYS.SALES);
  add(pendingFact(facts, "publication"), DASHBOARD_AREA_KEYS.PAGE);
  add(
    facts?.customDomain?.configured === true && facts?.customDomain?.complete !== true,
    DASHBOARD_AREA_KEYS.SETTINGS,
  );

  return result;
}

function areaStarterAreas(area) {
  const explicit = AREA_STARTER_AREAS[area?.key];
  if (explicit?.length) return [...explicit];
  const destinationAreas = navigationItemAreas(area?.destinationKey);
  return destinationAreas.length ? destinationAreas : [];
}

function starterRelevantAreaKeys(project, areas, availableKeys) {
  const starterAreas = new Set(projectStarterAreas(project));
  if (!starterAreas.size) return [];

  return (Array.isArray(areas) ? areas : [])
    .filter((area) => area && area.key !== DASHBOARD_AREA_KEYS.HOME)
    .filter((area) => areaStarterAreas(area).some((starterArea) => starterAreas.has(starterArea)))
    .map((area) => area.key)
    .filter((key) => availableKeys.has(key));
}

export function projectNavigationKey(project) {
  return String(project?.slug || project?.id || project?._id || "").trim();
}

function storageKey(project, user) {
  const projectKey = projectNavigationKey(project);
  const userKey = navigationUserKey(user);
  return projectKey && userKey ? `${STORAGE_PREFIX}:${userKey}:${projectKey}` : "";
}

function normalizeState(value) {
  const source = value && typeof value === "object" ? value : {};
  const exploredItems = Array.from(new Set(
    (Array.isArray(source.exploredItems) ? source.exploredItems : [])
      .map((item) => cleanItemKey(item))
      .filter(Boolean),
  )).slice(0, 80);
  const exploredAreas = Array.from(new Set(
    (Array.isArray(source.exploredAreas) ? source.exploredAreas : [])
      .map((item) => clean(item).toLowerCase())
      .filter((item) => PROJECT_AREA_KEYS.has(item)),
  )).slice(0, 24);

  return {
    exploredItems,
    exploredAreas,
    expanded: source.expanded === true,
  };
}

function serverNavigationLearning(project) {
  return normalizeState(project?.memberAccess?.navigationLearning || {});
}

function mergeNavigationState(serverState, localState) {
  const server = normalizeState(serverState);
  const local = normalizeState(localState);
  return normalizeState({
    exploredItems: [...server.exploredItems, ...local.exploredItems],
    exploredAreas: [...server.exploredAreas, ...local.exploredAreas],
    // "Más herramientas" es una preferencia de interfaz, no aprendizaje durable.
    expanded: local.expanded,
  });
}

export function loadProjectNavigationState(project, user = null) {
  const server = serverNavigationLearning(project);
  const key = storageKey(project, user);
  if (!key || typeof window === "undefined") return server;

  try {
    const local = JSON.parse(window.localStorage.getItem(key) || "{}");
    return mergeNavigationState(server, local);
  } catch (_error) {
    return server;
  }
}

function saveProjectNavigationState(project, user, state) {
  const key = storageKey(project, user);
  const normalized = normalizeState(state);
  if (!key || typeof window === "undefined") return normalized;

  try {
    const stored = normalizeState(JSON.parse(window.localStorage.getItem(key) || "{}"));
    const merged = normalizeState({
      exploredItems: [...stored.exploredItems, ...normalized.exploredItems],
      exploredAreas: [...stored.exploredAreas, ...normalized.exploredAreas],
      expanded: normalized.expanded,
    });
    window.localStorage.setItem(key, JSON.stringify(merged));
    return merged;
  } catch (_error) {
    // El aprendizaje principal también se persiste en backend; storage sólo
    // mantiene respuesta inmediata y la expansión local de la interfaz.
    return normalized;
  }
}

export function projectNavigationAreaExplored(state, areaKey) {
  const key = clean(areaKey).toLowerCase();
  return PROJECT_AREA_KEYS.has(key) && normalizeState(state).exploredAreas.includes(key);
}

export function projectNavigationItemExplored(state, itemKey) {
  const key = cleanItemKey(itemKey);
  return Boolean(key && normalizeState(state).exploredItems.includes(key));
}

export function markProjectNavigationItemExplored(project, itemKey, currentState, user = null) {
  const normalized = normalizeState(currentState);
  const key = cleanItemKey(itemKey);
  if (!key || normalized.exploredItems.includes(key)) return normalized;

  return saveProjectNavigationState(project, user, {
    ...normalized,
    exploredItems: [...normalized.exploredItems, key],
  });
}

export function markProjectNavigationAreaExplored(project, areaKey, currentState, user = null) {
  const normalized = normalizeState(currentState);
  const key = clean(areaKey).toLowerCase();
  if (!PROJECT_AREA_KEYS.has(key) || normalized.exploredAreas.includes(key)) return normalized;

  return saveProjectNavigationState(project, user, {
    ...normalized,
    exploredAreas: [...normalized.exploredAreas, key],
  });
}

export function setProjectNavigationExpanded(project, expanded, currentState, user = null) {
  return saveProjectNavigationState(project, user, {
    ...normalizeState(currentState),
    expanded: expanded === true,
  });
}

export function persistProjectNavigationExploration(project, { areaKey = "", itemKey = "" } = {}) {
  const projectKey = projectNavigationKey(project);
  if (!projectKey) return Promise.resolve(null);

  const payload = {};
  const area = clean(areaKey).toLowerCase();
  const item = cleanItemKey(itemKey);
  if (PROJECT_AREA_KEYS.has(area)) payload.areaKey = area;
  if (item) payload.itemKey = item;
  if (!payload.areaKey && !payload.itemKey) return Promise.resolve(null);

  return projectsApi.markNavigationExplored(projectKey, payload);
}

export function usesProgressiveProjectNavigation(project, { superAdmin = false } = {}) {
  return !superAdmin && hasGuidedProjectStart(project) && isTrialProject(project);
}

function onboardingDiscoveryToolKeys(project, availableKeys, tools) {
  const result = [];
  const add = (key) => addUnique(result, key, availableKeys);

  // Capacidades activas son la señal más directa de una herramienta elegida o
  // sugerida durante el alta. Cada capacidad apunta a una única entrada útil.
  for (const [capability, toolKey] of Object.entries(CAPABILITY_DISCOVERY_TOOL_KEYS)) {
    if (isProjectCapabilityEnabled(project, capability)) add(toolKey);
  }

  const profile = normalizeProjectBusinessProfile(project?.businessProfile);
  const goals = projectBusinessGoals(project);
  for (const goal of goals) {
    if (goal === "takeReservations") {
      add(profile === "restaurant" ? "gastronomy-reservations" : "agenda");
      continue;
    }
    for (const toolKey of GOAL_DISCOVERY_TOOL_KEYS[goal] || []) add(toolKey);
  }

  if (!goals.length) {
    for (const toolKey of PROFILE_DISCOVERY_TOOL_KEYS[profile] || []) add(toolKey);
  }

  const starterAreas = new Set(projectStarterAreas(project));
  for (const tool of tools) {
    const starter = Array.isArray(tool?.starterAreas) && tool.starterAreas.length
      ? tool.starterAreas
      : navigationItemAreas(tool?.key);
    if (starter.some((area) => starterAreas.has(area))) add(tool?.key);
  }

  return result;
}

function readinessDiscoveryToolKeys(project, availableKeys) {
  const facts = project?.readiness?.facts && typeof project.readiness.facts === "object"
    ? project.readiness.facts
    : {};
  if (!Object.keys(facts).length) return [];

  const result = [];
  const add = (condition, key) => {
    if (condition) addUnique(result, key, availableKeys);
  };

  add(pendingFact(facts, "identity"), "settings-general");
  add(pendingFact(facts, "pageStarted"), "page-publication");
  add(pendingFact(facts, "firstItem"), "products");
  add(pendingFact(facts, "paymentMethod"), "ecommerce");
  add(pendingFact(facts, "publication"), "page-publication");

  const operational = facts?.operationalManagement && typeof facts.operationalManagement === "object"
    ? facts.operationalManagement
    : {};
  const milestones = operational?.milestones && typeof operational.milestones === "object"
    ? operational.milestones
    : {};

  for (const [code, toolKey] of Object.entries(READINESS_MILESTONE_TOOL_KEYS)) {
    add(pendingMilestone(milestones, code), toolKey);
  }

  if (!Object.keys(milestones).length) {
    const targets = operational?.targets && typeof operational.targets === "object"
      ? operational.targets
      : {};
    const evidence = operational?.evidence && typeof operational.evidence === "object"
      ? operational.evidence
      : {};
    add(targets.customers === true && evidence.customers !== true, "customers-crm");
    add(targets.reservations === true && evidence.reservations !== true, "agenda");
    add(targets.gastronomy === true && evidence.gastronomy !== true, "gastronomy-menu");
    add(targets.system === true && evidence.system !== true, "overview");
  }

  return result;
}

export function projectNavigationUnseenToolKeys(project, tools = [], currentState = {}) {
  const visibleTools = (Array.isArray(tools) ? tools : []).filter(
    (tool) => tool?.key && tool.visible !== false,
  );
  const availableKeys = new Set(visibleTools.map((tool) => tool.key));
  const explored = new Set(normalizeState(currentState).exploredItems);
  const active = new Set(visibleTools.filter((tool) => tool.active).map((tool) => tool.key));
  const candidates = [];

  for (const key of onboardingDiscoveryToolKeys(project, availableKeys, visibleTools)) {
    addUnique(candidates, key, availableKeys);
  }
  for (const key of readinessDiscoveryToolKeys(project, availableKeys)) {
    addUnique(candidates, key, availableKeys);
  }

  return candidates
    .filter((key) => !explored.has(key) && !active.has(key))
    .slice(0, MAX_NEW_TOOL_INDICATORS);
}

export function projectNavigationNeededAreaKeys(project, areas = [], currentState = {}) {
  const visibleAreas = Array.isArray(areas) ? areas.filter(Boolean) : [];
  const availableKeys = new Set(visibleAreas.map((area) => area.key));
  const explored = new Set(normalizeState(currentState).exploredAreas);
  const result = [];

  // Las capacidades específicas del negocio tienen un lugar visible desde el
  // comienzo, aunque la prueba incluya muchas otras herramientas.
  for (const key of capabilityAnchorAreaKeys(project, availableKeys)) {
    if (explored.has(key)) continue;
    addUnique(result, key, availableKeys);
  }

  // Readiness aporta los próximos hitos reales. Se limita la cantidad de áreas
  // pendientes para evitar convertir la guía en otra lista interminable.
  for (const key of readinessAreaKeys(project, availableKeys)) {
    if (result.length >= MAX_GUIDED_PENDING_AREAS) break;
    if (explored.has(key)) continue;
    addUnique(result, key, availableKeys);
  }

  // Para readiness legacy o todavía no recalculado, completar con las áreas que
  // el onboarding/capacidades ya declararon relevantes. Las visitadas no ocupan
  // estos cupos porque ya se promocionan por separado.
  for (const key of starterRelevantAreaKeys(project, visibleAreas, availableKeys)) {
    if (result.length >= MAX_GUIDED_PENDING_AREAS) break;
    if (explored.has(key)) continue;
    addUnique(result, key, availableKeys);
  }

  return result.slice(0, MAX_GUIDED_PENDING_AREAS);
}

export function splitProjectNavigationAreas(project, areas = [], state = {}, enabled = true) {
  const visibleAreas = Array.isArray(areas) ? areas.filter(Boolean) : [];
  if (!enabled) {
    return {
      primary: visibleAreas,
      secondary: [],
      expanded: true,
      neededAreaKeys: [],
    };
  }

  const normalized = normalizeState(state);
  const explored = new Set(normalized.exploredAreas);
  const neededAreaKeys = projectNavigationNeededAreaKeys(project, visibleAreas, normalized);
  const needed = new Set(neededAreaKeys);
  const primary = [];
  const secondary = [];

  for (const area of visibleAreas) {
    const prioritized = (
      area.key === DASHBOARD_AREA_KEYS.HOME ||
      area.active ||
      explored.has(area.key) ||
      needed.has(area.key)
    );
    (prioritized ? primary : secondary).push(area);
  }

  if (!primary.length && secondary.length) primary.push(secondary.shift());

  return {
    primary,
    secondary,
    expanded: normalized.expanded,
    neededAreaKeys,
  };
}

export function splitProjectNavigationItems(project, items = [], state = {}, enabled = true) {
  const visibleItems = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!enabled) {
    return { primary: visibleItems, secondary: [], expanded: true };
  }

  const normalized = normalizeState(state);
  const starterAreas = new Set(projectStarterAreas(project));
  const explored = new Set(normalized.exploredItems);
  const primary = [];
  const secondary = [];

  for (const item of visibleItems) {
    const areas = Array.isArray(item.starterAreas) && item.starterAreas.length
      ? item.starterAreas
      : navigationItemAreas(item.key);
    const prioritized = item.active || explored.has(item.key) || areas.some((area) => starterAreas.has(area));
    (prioritized ? primary : secondary).push(item);
  }

  if (!primary.length && secondary.length) primary.push(secondary.shift());

  return {
    primary,
    secondary,
    expanded: normalized.expanded,
  };
}
